/**
 * Broad-search resolve client for POST /api/broad/resolve (2026-08-01).
 *
 * Called SSR from /compare when the build-time BROAD_SEARCH_ENABLED flag is on.
 * Returns the endpoint's decision:
 *   - "exact"        -> fall through to today's comparison flow
 *   - "picks"        -> render the curated best 5-6 (FastResults)
 *   - "ask"          -> the "tell me the exact product" screen
 *   - "out_of_scope" -> "we don't compare X yet"
 *
 * Fail-safe: NEVER throws. On any error, non-200, or timeout it returns
 * {mode:"exact"} so the caller falls through to today's comparison flow, never
 * a crash. The backend returns {mode:"exact", spent:0} when the flag is off.
 */

import { bearerHeaders } from "@/lib/auth-bearer";
import { proofHeaders } from "@/lib/human-proof";

import { getInternalAuthHeaders } from "./internal-headers";

function apiBaseUrl(): string {
  if (typeof window === "undefined") {
    return (
      process.env.API_INTERNAL_URL ??
      process.env.API_URL ??
      "http://localhost:8000"
    );
  }
  return process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
}

export interface BroadProduct {
  title: string;
  brand: string;
  price_aud: number;
  price_display: string;
  shop: string;
  shop_count: number;
  rating: number | null;
  reviews_cnt: number | null;
  image: string | null;
  /** 2026-08-16: the saved /c/<slug> product comparison for this pick, set by
   *  background warming once a REAL comparison exists. When present, the list
   *  card links straight to /c/<slug> (instant, cache-only); when null, it falls
   *  back to a live /compare search. Absent on old cache rows -> treated as null. */
  comparison_slug?: string | null;
}

export type BroadMode = "exact" | "picks" | "ask" | "out_of_scope";

export interface BroadResolveResponse {
  mode: BroadMode;
  heading: string | null;
  product_label: string | null;
  slug: string | null;
  products: BroadProduct[];
  out_of_scope_reason: string | null;
}

// The picks path makes a cold Google Shopping call (measured 6-19s, server
// caps it at ~25s then abstains). Give the SSR fetch headroom over that; on a
// timeout we fall through to today's flow rather than hang.
const BROAD_TIMEOUT_MS = 30_000;

const EXACT_FALLBACK: BroadResolveResponse = {
  mode: "exact",
  heading: null,
  product_label: null,
  slug: null,
  products: [],
  out_of_scope_reason: null,
};

/** POST /api/broad/resolve. Never throws (returns the exact pass-through on any failure). */
export async function resolveBroad(
  query: string,
  countryCode = "AU",
): Promise<BroadResolveResponse> {
  const url = `${apiBaseUrl()}/api/broad/resolve`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), BROAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getInternalAuthHeaders(),
      },
      body: JSON.stringify({ query, country_code: countryCode }),
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return EXACT_FALLBACK;
    const data = (await response.json()) as Partial<BroadResolveResponse>;
    return {
      mode: (data.mode as BroadMode) ?? "exact",
      heading: data.heading ?? null,
      product_label: data.product_label ?? null,
      slug: data.slug ?? null,
      products: Array.isArray(data.products) ? data.products : [],
      out_of_scope_reason: data.out_of_scope_reason ?? null,
    };
  } catch {
    return EXACT_FALLBACK;
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface BroadPageResponse {
  products: BroadProduct[];
  is_stale: boolean;
  is_very_stale: boolean;
  fetched_at: string | null;
}

/**
 * GET /api/broad/page/{slug} (Phase C). Returns the saved broad picks for a list
 * slug, or `null` when this is not a broad page (404) / the flag is off / any
 * error - the /c/list route then falls back to today's deep-list render. Never
 * throws.
 */
export async function getBroadPage(
  slug: string,
  countryCode = "AU",
): Promise<BroadPageResponse | null> {
  const url =
    `${apiBaseUrl()}/api/broad/page/${encodeURIComponent(slug)}` +
    `?country_code=${encodeURIComponent(countryCode)}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), BROAD_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { ...getInternalAuthHeaders() },
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Partial<BroadPageResponse>;
    if (!Array.isArray(data.products) || data.products.length === 0) return null;
    return {
      products: data.products,
      is_stale: Boolean(data.is_stale),
      is_very_stale: Boolean(data.is_very_stale),
      fetched_at: data.fetched_at ?? null,
    };
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

export interface WarmOneResult {
  // exists     -> already built: open /c/<slug> instantly ($0, no scrape)
  // built      -> built now from the pick's cluster: open /c/<slug>
  // unwarmable -> thin/unresolvable: caller falls back to the live search
  status: "exists" | "built" | "unwarmable";
  slug: string | null;
}

// Warming one pick scrapes merchant pages, measured seconds; give it headroom
// over the per-shop fetch window. On timeout we return null and the caller runs
// the live /compare fallback rather than hanging on the tap.
const WARM_ONE_TIMEOUT_MS = 30_000;

/**
 * POST /api/broad/warm-one (Phase 2). Build (or reuse) the tapped product's
 * comparison. Called from the BROWSER on a tap. Returns the result, or `null`
 * on any non-200 / timeout / error so the caller can fall back to the live
 * /compare search. NEVER throws.
 *
 * The pick is identified server-side by (list_slug, exact title); the client
 * does not send the google_link, so a tampered request cannot warm an
 * arbitrary URL.
 */
export async function warmOne(
  listSlug: string,
  title: string,
  countryCode = "AU",
): Promise<WarmOneResult | null> {
  const url = `${apiBaseUrl()}/api/broad/warm-one`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), WARM_ONE_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getInternalAuthHeaders(),
        ...(await proofHeaders()),
        ...(await bearerHeaders()),
      },
      body: JSON.stringify({
        list_slug: listSlug,
        title,
        country_code: countryCode,
      }),
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Partial<WarmOneResult>;
    if (data.status !== "exists" && data.status !== "built" && data.status !== "unwarmable") {
      return null;
    }
    return { status: data.status, slug: data.slug ?? null };
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * POST /api/broad/compare-from-offers (Phase 2, 2026-08-19). The GUARANTEED tap:
 * build (or reuse) the tapped product's comparison DIRECTLY from the offers the
 * category build already saved - no re-scrape, ~instant. Returns the /c slug, or
 * `null` on any non-200 / timeout / error so the caller can fall back to the live
 * /compare search. NEVER throws. The pick is identified server-side by
 * (list_slug, exact title); the client sends no offers, so a tampered request
 * cannot build an arbitrary page.
 */
export async function compareFromOffers(
  listSlug: string,
  title: string,
  countryCode = "AU",
): Promise<WarmOneResult | null> {
  const url = `${apiBaseUrl()}/api/broad/compare-from-offers`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), WARM_ONE_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getInternalAuthHeaders(),
        ...(await proofHeaders()),
        ...(await bearerHeaders()),
      },
      body: JSON.stringify({ list_slug: listSlug, title, country_code: countryCode }),
      cache: "no-store",
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Partial<WarmOneResult>;
    if (data.status !== "exists" && data.status !== "built" && data.status !== "unwarmable") {
      return null;
    }
    return { status: data.status, slug: data.slug ?? null };
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
